// Repository interface — local IndexedDB by default; the auth layer can swap
// in a SyncedRepository that mirrors writes to the user's cloud row.

import type { ChatState, LibraryWord, Persona, PersonaMemory, Relationship, UserProfile } from '../types';

export type AIProvider = 'anthropic' | 'deepseek';

export interface AppSnapshot {
  version: number;
  profile: UserProfile;
  /** Which AI vendor handles live chat + suggestions. Default: deepseek. */
  aiProvider: AIProvider;
  /** User chose "Continue without account" — don't auto-show the sign-in gate. */
  skipAuth: boolean;
  /** Soft sign-in nudge dismissed; never auto-show again until user opens Settings. */
  nudgeDismissed: boolean;
  /** True once the user has passed the pre-auth welcome screen (either by
   *  watching the demo or jumping straight to sign-in). Gates whether the
   *  two-option welcome screen appears on subsequent visits. */
  hasSeenWelcome: boolean;
  /** True if a signed-in user explicitly skipped the post-sign-in profile
   *  setup (name / level / skills / goals). Prevents the prompt from re-
   *  appearing on every render. */
  skippedSetup: boolean;
  /** ISO timestamp of the last in-memory mutation. Used by sync merge to pick newer side. */
  savedAt: string;
  /** Words saved from chat bubbles + the library + button. One pool across all personas. */
  library: LibraryWord[];
  /** Words the learner marked as known. One pool across all personas. */
  mastered: LibraryWord[];
  /** Per-persona match/skip + warmth tracking. Covers curated AND user-built personas. */
  relationships: Record<string, Relationship>;
  /** Per-persona chat history. One pool across all personas. */
  chatStates: Record<string, ChatState>;
  /**
   * Map of personaId → the story-batch date (UTC YYYY-MM-DD) the user viewed.
   * The DiscoverScreen compares this against today's batch date so the
   * Instagram-style "watched" ring fades only for today's stories and goes
   * back to vibrant when a fresh batch arrives the next day.
   */
  viewedStories: Record<string, string>;
  /** Persona IDs the user has starred. */
  favorites: Record<string, true>;
  /**
   * Per-persona durable facts about the user, periodically extracted from
   * recent chat history. Lets the persona "remember" who the user is even
   * after old turns slide out of the prompt window.
   */
  personaMemories: Record<string, PersonaMemory>;
  /** User-built personas, created from the "+" button. Joined into the unified pool. */
  customPersonas: Persona[];
}

export interface Repository {
  load(): Promise<AppSnapshot>;
  save(snapshot: AppSnapshot): Promise<void>;
  /** Subscribe to remote/local changes. Returns an unsubscribe fn. */
  subscribe?(handler: (s: AppSnapshot) => void): () => void;
}

export const SCHEMA_VERSION = 11;

export const EMPTY_SNAPSHOT: AppSnapshot = {
  version: SCHEMA_VERSION,
  profile: { userId: '', streak: 0 },
  aiProvider: 'deepseek',
  skipAuth: false,
  nudgeDismissed: false,
  hasSeenWelcome: false,
  skippedSetup: false,
  savedAt: new Date(0).toISOString(),
  library: [],
  mastered: [],
  relationships: {},
  chatStates: {},
  viewedStories: {},
  favorites: {},
  personaMemories: {},
  customPersonas: [],
};
